import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Loader2, Check, ArrowLeft, Coins, CreditCard, Shield, Zap } from 'lucide-react';
import { getPaymentPackages, processPayment } from '@/api/apiService';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

interface Package {
  id: string;
  name: string;
  description: string;
  tokens: number;
  price: number;
  price_cents: number;
}

interface PaymentResult {
  success: boolean;
  payment_id?: string;
  tokens_added?: number;
  new_token_balance?: number;
  package?: string;
}

const CheckoutHeader = () => {
  return (
      <header className="bg-background border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/profile" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span className="font-medium">Back</span>
            </Link>

            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">U</span>
              </div>
              <span className="font-bold text-xl">Ulixes</span>
            </div>

            <div className="w-16"></div> {/* Spacer for centering */}
          </div>
        </div>
      </header>
  );
};

const PackageDetails = ({ selectedPackage }: { selectedPackage: Package }) => {
  const getPackageFeatures = (packageId: string) => {
    switch (packageId) {
      case 'starter':
        return [
          'Perfect for trying out Ulixes',
          'Create 5 travel guides',
          'Basic AI recommendations',
          'Standard support'
        ];
      case 'popular':
        return [
          'Most popular choice',
          'Create 15 travel guides',
          'Enhanced AI recommendations',
          'Priority support',
          'Save guides offline'
        ];
      case 'premium':
        return [
          'Best value for frequent travelers',
          'Create 50 travel guides',
          'Premium AI recommendations',
          '24/7 priority support',
          'Unlimited guide revisions',
          'Export to multiple formats'
        ];
      default:
        return [];
    }
  };

  const features = getPackageFeatures(selectedPackage.id);
  const isPopular = selectedPackage.id === 'popular';

  return (
      <div className="space-y-6">
        <div className="relative">
          {isPopular && (
              <Badge className="absolute -top-2 left-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white border-0">
                Most Popular
              </Badge>
          )}
          <Card className="border-2 border-primary/20">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center mb-4">
                <Coins className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">{selectedPackage.name}</CardTitle>
              <CardDescription className="text-base">{selectedPackage.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-primary">${selectedPackage.price}</div>
                <div className="text-muted-foreground">One-time payment</div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  What's included:
                </h4>
                <ul className="space-y-3">
                  {features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                  ))}
                </ul>
              </div>

              <Separator />

              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total tokens:</span>
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-primary" />
                    <span className="font-bold text-lg">{selectedPackage.tokens}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">Secure Payment</p>
                <p className="text-blue-700 dark:text-blue-200">Your payment is protected by Stripe's industry-leading security.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
  );
};

const CheckoutForm = ({ selectedPackage, clientSecret }: { selectedPackage: Package; clientSecret: string }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<PaymentResult | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment method from PaymentElement
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Failed to submit payment details');
        setLoading(false);
        return;
      }

      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message || 'Failed to create payment method');
        setLoading(false);
        return;
      }

      // Check if this is a payment method that requires redirection (like PayPal)
      const redirectPaymentMethods = ['paypal', 'klarna', 'afterpay_clearpay', 'alipay'];

      if (redirectPaymentMethods.includes(paymentMethod.type)) {
        // For redirect payment methods, try to process with backend first
        try {
          const result = await processPayment(paymentMethod.id, selectedPackage.id);

          if (result?.payment_intent) {
            // If backend returns a payment_intent client_secret, use it for confirmation
            const { error } = await stripe.confirmPayment({
              elements,
              clientSecret: result.payment_intent,
              confirmParams: {
                return_url: `${window.location.origin}/payment/complete`,
              },
            });

            if (error) {
              setError(error.message || 'Payment confirmation failed');
            }
            // If no error, user will be redirected to payment provider
          } else if (result?.success) {
            // Payment succeeded immediately
            setSuccess(result);
            setTimeout(() => {
              navigate('/profile');
            }, 2000);
          } else {
            setError(result?.error || 'Payment failed for this payment method');
          }
        } catch (backendError: any) {
          setError('Payment processing failed. Please try a different payment method.');
        }
      } else {
        // For card payments, use the existing backend flow
        const result = await processPayment(paymentMethod.id, selectedPackage.id);

        if (result?.success) {
          setSuccess(result);
          // Redirect to profile after 2 seconds
          setTimeout(() => {
            navigate('/profile');
          }, 2000);
        } else if (result?.error) {
          // Handle authentication required case
          if (result.error.includes('requires additional authentication') && result.payment_intent) {
            const { error: confirmError } = await stripe.confirmCardPayment(result.payment_intent);
            if (confirmError) {
              setError(confirmError.message || 'Authentication failed');
            } else {
              // Try processing again after authentication
              const retryResult = await processPayment(paymentMethod.id, selectedPackage.id);
              if (retryResult?.success) {
                setSuccess(retryResult);
                setTimeout(() => {
                  navigate('/profile');
                }, 2000);
              } else {
                setError(retryResult?.error || 'Payment failed after authentication');
              }
            }
          } else {
            setError(result.error);
          }
        } else {
          setError('Payment failed. Please try again.');
        }
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.response?.data?.error || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
        <Card className="w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900 dark:to-green-800 rounded-full flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-2xl text-green-600 dark:text-green-400 mb-2">Payment Successful!</CardTitle>
            <CardDescription className="text-base">
              {success.tokens_added} tokens have been added to your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="font-medium">Package:</span>
                <span className="text-green-700 dark:text-green-300">{success.package}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium">New balance:</span>
                <div className="flex items-center gap-2">
                  <Coins className="w-4 h-4 text-primary" />
                  <span className="font-bold">{success.new_token_balance} tokens</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center">Payment ID: {success.payment_id}</p>
            <p className="text-sm text-center text-muted-foreground">Redirecting to your profile...</p>
          </CardContent>
        </Card>
    );
  }

  return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Payment Details</CardTitle>
              <CardDescription>
                Complete your purchase securely
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Order Summary</span>
                </div>
                <div className="flex justify-between items-center text-sm text-muted-foreground mb-1">
                  <span>{selectedPackage.name}</span>
                  <span>${selectedPackage.price}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-muted-foreground">
                  <span>{selectedPackage.tokens} tokens</span>
                </div>
                <Separator className="my-3" />
                <div className="flex justify-between items-center font-medium">
                  <span>Total</span>
                  <span className="text-lg">${selectedPackage.price}</span>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-base font-medium">Payment Information</label>
                <div className="p-6 border-2 border-input rounded-lg focus-within:border-primary transition-colors">
                  <PaymentElement
                      options={{
                        layout: 'tabs',
                      }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Choose your preferred payment method and enter your details
                </p>
              </div>
            </div>

            {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <Button
                type="submit"
                disabled={!stripe || loading}
                className="w-full h-12 text-base font-medium"
                size="lg"
            >
              {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing Payment...
                  </>
              ) : (
                  <>
                    <Shield className="mr-2 h-5 w-5" />
                    Pay ${selectedPackage.price} Securely
                  </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              By completing your purchase, you agree to our Terms of Service
            </p>
          </form>
        </CardContent>
      </Card>
  );
};

const Checkout = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  const stripePromise = loadStripe("pk_test_51RVXuwDIOzr6FXQXth2hVJqhdZo20hnUiIVmsM9mXzbjXr9SP9S0GkSqSpA6r8kgbW8YRCYgFmL9leSfExuoda070061WCtpq6");

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const data = await getPaymentPackages();
        setPackages(data);

        // Check URL parameter for plan selection
        const planParam = searchParams.get('plan');
        let selectedId: string;
        if (planParam && data.find(pkg => pkg.id === planParam)) {
          selectedId = planParam;
        } else if (data.length > 0) {
          selectedId = data[1]?.id || data[0].id; // Default to popular package
        }

        setSelectedPackageId(selectedId);

        // Set a placeholder client secret to enable PaymentElement
        setClientSecret('placeholder');
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [searchParams]);

  const selectedPackage = packages?.find(pkg => pkg.id === selectedPackageId);

  if (loading) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
          <CheckoutHeader />
          <div className="container mx-auto py-16 flex justify-center">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
              <p className="text-muted-foreground">Loading payment options...</p>
            </div>
          </div>
        </div>
    );
  }

  if (!selectedPackage) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
          <CheckoutHeader />
          <div className="container mx-auto py-16 flex justify-center">
            <Card className="w-full max-w-md">
              <CardContent className="text-center py-16">
                <p className="text-muted-foreground mb-4">Package not found</p>
                <Link to="/profile">
                  <Button>Return to Profile</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
        <CheckoutHeader />
        <div className="container mx-auto py-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold mb-2">Complete Your Purchase</h1>
                  <p className="text-muted-foreground">Choose your token package and start creating amazing travel guides</p>
                </div>
                <PackageDetails selectedPackage={selectedPackage} />
              </div>

              <div className="lg:flex lg:items-center lg:min-h-[80vh]">
                <div className="w-full">
                  {clientSecret ? (
                      <Elements
                          stripe={stripePromise}
                          options={{
                            mode: 'payment',
                            amount: selectedPackage.price * 100,
                            currency: 'usd',
                            paymentMethodCreation: 'manual',
                            appearance: {
                              theme: 'stripe',
                              variables: {
                                colorPrimary: 'hsl(var(--primary))',
                                colorBackground: 'hsl(var(--background))',
                                colorText: 'hsl(var(--foreground))',
                                colorDanger: 'hsl(var(--destructive))',
                                fontFamily: 'system-ui, sans-serif',
                                spacingUnit: '4px',
                                borderRadius: '8px',
                              },
                            },
                          }}
                      >
                        <CheckoutForm selectedPackage={selectedPackage} clientSecret={clientSecret} />
                      </Elements>
                  ) : (
                      <Card className="w-full">
                        <CardContent className="text-center py-16">
                          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
                          <p className="text-muted-foreground">Setting up payment...</p>
                        </CardContent>
                      </Card>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Checkout;
